<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const CAPTAIN_AVAILABLE_TOOLS = [
  { id: 'search_documentation', name: 'SEARCH_DOCUMENTATION' },
  { id: 'get_article', name: 'GET_ARTICLE' },
  { id: 'get_contact', name: 'GET_CONTACT' },
  { id: 'get_conversation', name: 'GET_CONVERSATION' },
  { id: 'search_articles', name: 'SEARCH_ARTICLES' },
  { id: 'search_contacts', name: 'SEARCH_CONTACTS' },
  { id: 'search_conversations', name: 'SEARCH_CONVERSATIONS' },
  { id: 'search_linear_issues', name: 'SEARCH_LINEAR_ISSUES' },
];

const initialState = {
  selectedTools: [],
  forceToolCall: false,
};

const state = reactive({ ...initialState });

const isToolSelectionEmpty = computed(() => state.selectedTools.length === 0);

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.selectedTools = config.selected_tools || [];
  state.forceToolCall = config.force_tool_call || false;
};

const handleToolsUpdate = () => {
  const payload = {
    config: {
      ...props.assistant.config,
      selected_tools: state.selectedTools,
      force_tool_call: state.forceToolCall,
    },
  };

  emit('submit', payload);
};

const toggleAllTools = (selectAll = true) => {
  state.selectedTools = selectAll
    ? CAPTAIN_AVAILABLE_TOOLS.map(tool => tool.id)
    : [];
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);

watch(
  () => state.selectedTools,
  () => {
    if (isToolSelectionEmpty.value) {
      state.forceToolCall = false;
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <p class="text-sm text-n-slate-11">
      {{ t('CAPTAIN.ASSISTANTS.FORM.TOOLS.DESCRIPTION') }}
    </p>

    <div class="flex gap-2 mb-2">
      <Button
        size="sm"
        variant="outline"
        :label="t('CAPTAIN.ASSISTANTS.FORM.TOOLS.SELECT_ALL')"
        @click="toggleAllTools(true)"
      />
      <Button
        size="sm"
        variant="outline"
        :label="t('CAPTAIN.ASSISTANTS.FORM.TOOLS.DESELECT_ALL')"
        @click="toggleAllTools(false)"
      />
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
      <label
        v-for="tool in CAPTAIN_AVAILABLE_TOOLS"
        :key="tool.id"
        class="flex items-center gap-2"
      >
        <input
          v-model="state.selectedTools"
          :value="tool.id"
          type="checkbox"
          class="form-checkbox"
        />
        {{ t(`CAPTAIN.ASSISTANTS.FORM.TOOLS.ITEMS.${tool.name}`) }}
      </label>
    </div>

    <div class="flex flex-col gap-2">
      <label
        class="flex items-center gap-2"
        :class="{ 'opacity-50 cursor-not-allowed': isToolSelectionEmpty }"
      >
        <input
          v-model="state.forceToolCall"
          type="checkbox"
          class="form-checkbox"
          :disabled="isToolSelectionEmpty"
        />
        {{ t('CAPTAIN.ASSISTANTS.FORM.TOOLS.FORCE_TOOL_CALL.LABEL') }}
      </label>
      <p class="text-sm text-n-slate-11 italic">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TOOLS.FORCE_TOOL_CALL.DESCRIPTION') }}
      </p>
    </div>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleToolsUpdate"
      />
    </div>
  </div>
</template>
